import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { getPipelines, deletePipeline } from '@/api/pipeline';
import { Pipeline } from '@/types/pipeline';

const PipelineList: React.FC = () => {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pipelineToDelete, setPipelineToDelete] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchPipelines = async () => {
    try {
      const data = await getPipelines();
      setPipelines(data);
    } catch (err) {
      console.error('Error fetching pipelines:', err);
      setError('Failed to load pipelines');
      setPipelines([]);
    }
  };

  useEffect(() => {
    fetchPipelines();
  }, []);

  const handleDeleteClick = (e: React.MouseEvent, pipelineId: string) => {
    e.stopPropagation(); // Prevent row click
    setPipelineToDelete(pipelineId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!pipelineToDelete) return;

    try {
      await deletePipeline(pipelineToDelete);
      await fetchPipelines(); // Refresh the list
    } catch (err) {
      console.error('Error deleting pipeline:', err);
      setError('Failed to delete pipeline');
    } finally {
      setDeleteDialogOpen(false);
      setPipelineToDelete(null);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>Pipelines</Typography>
      <Button variant="contained" color="primary" onClick={() => navigate('/pipelines/new')} sx={{ mb: 2 }}>
        New Pipeline
      </Button>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Updated At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pipelines.map((pipeline) => (
              <TableRow 
                key={pipeline.id} 
                hover 
                onClick={() => navigate(`/pipelines/${pipeline.id}`)} 
                style={{ cursor: 'pointer' }}
              >
                <TableCell>{pipeline.name}</TableCell>
                <TableCell>{pipeline.status}</TableCell>
                <TableCell>{new Date(pipeline.created_at).toLocaleString()}</TableCell>
                <TableCell>{new Date(pipeline.updated_at).toLocaleString()}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={(e) => handleDeleteClick(e, pipeline.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Pipeline</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this pipeline? This will also delete all associated jobs and their results. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PipelineList; 